import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner
} from 'reactstrap';
import MedDataTable from '../common/MedDataTable';
import TablePageSizeSelect from '../common/TablePageSizeSelect';
import AnalysisSegmentCharts from './AnalysisSegmentCharts';
import useStoredTablePageSize from '../../hooks/useStoredTablePageSize';
import apiBase from '../../apiConfig';
import { formatSelectedPeriod } from '../../helpers/testPeriod';
import { getAuthHeaders } from '../../helpers/utils';

const FACULTIES_ENDPOINT = `${apiBase}/p-admin/depressiya.php?action=faculties`;
const GROUP_ENDPOINT = `${apiBase}/p-admin/depressiya.php?action=group`;

const DEFAULT_DEPRESSION_LEVELS = [
  {
    natija: 3,
    title: 'Yaxshi',
    range_label: '0-7 ball',
    modal_title: 'Yaxshi 0-7 ball',
    label: 'Hayotidan mamnun va baxtiyor',
    summary: 'hayotidan mamnun va baxtiyor',
    description:
      'Siz hayotingizdan mamnun, baxtiyor insonsiz. Depressiya xavfi sizda deyarli ko‘zga tashlanmaydi. O‘zingiz, yaqinlaringiz, do‘stlaringiz bilan kelishgan holda to‘g‘ri hayot tarzini kechira olasiz. Hayotning har bir kichik quvonchidan shodlana bilasiz. Lekin ko‘ngilsizliklar ham oz bo‘lsa-da qalbingizda iz qoldiradi.',
    guidance:
      'Depressiya tashhisidan so‘ng vrach bemor holatining qanchalik og‘irligiga e’tibor berishi lozim. Bemorlarda suitsidal urinishlar bo‘lganda og‘ir depressiyada, psixotrop vositalar effekti bo‘lmaganda, bemorga psixiatrga yo‘llanma beriladi. Boshqa holatlarda vrach-internist antidepressiv terapiya tavsiya qiladi.',
    tone: 'success',
    display_order: 1
  },
  {
    natija: 2,
    title: 'O‘rta',
    range_label: '8-14 ball',
    modal_title: 'O‘rta 8-14 ball',
    label: 'Ruhiy siqilish o‘rtacha kechadi',
    summary: 'ruhiy siqilish o‘rtacha kechadi',
    description:
      'Ruhiy siqilish sizda o‘rtacha kechadi deyish mumkin. Shunday paytlar bo‘ladiki, arzimagan muammolar tufayli ruhiy tushkunlikka tushib qolasiz. Dunyoga sig‘may ketasiz. Faqat yolg‘iz qolgingiz kelaveradi. Ammo bu holat sizda uzoq davom etmaydi. Tezda o‘zingizni qo‘lga olib bu muammolarni unutasiz.',
    guidance:
      'Depressiya tashhisidan so‘ng vrach bemor holatining qanchalik og‘irligiga e’tibor berishi lozim. Bemorlarda suitsidal urinishlar bo‘lganda og‘ir depressiyada, psixotrop vositalar effekti bo‘lmaganda, bemorga psixiatrga yo‘llanma beriladi. Boshqa holatlarda vrach-internist antidepressiv terapiya tavsiya qiladi.',
    tone: 'info',
    display_order: 2
  },
  {
    natija: 1,
    title: 'Yuqori',
    range_label: '15-20 ball',
    modal_title: 'Yuqori 15 va undan ortiq ball',
    label: 'Fe’lingiz tor va deyarli har kuni ruhan siqiladi',
    summary: 'fe’lingiz tor va deyarli har kuni ruhan siqiladi',
    description:
      'Afsuski, sizning fe’lingiz tor va deyarli har kuni ruhan siqilasiz. Koshkiydi bu holat qisqa muddatda barham topsa. Yo‘q bir siqilsangiz, tamom, anchagacha o‘zingizga kela olmaysiz. Atrofdagilarning gap-so‘zi ham, yayrab kulishlari, sizni ovutishga urinishlari, umuman ortiqcha shovqinga toqat qila olmaysiz. Hoynahoy, qandaydir jiddiy muammo sizni shu ko‘yga solib qo‘ygan bo‘lsa kerak. Unutmang bu holat bir kun kelib, salomatligingizga salbiy ta’sir ko‘rsatishi mumkin. Shuning uchun sizga ruhshunos qabuliga borishni maslahat beramiz.',
    guidance:
      'Depressiya tashhisidan so‘ng vrach bemor holatining qanchalik og‘irligiga e’tibor berishi lozim. Bemorlarda suitsidal urinishlar bo‘lganda og‘ir depressiyada, psixotrop vositalar effekti bo‘lmaganda, bemorga psixiatrga yo‘llanma beriladi. Boshqa holatlarda vrach-internist antidepressiv terapiya tavsiya qiladi.',
    tone: 'danger',
    display_order: 3
  }
];

const DEFAULT_UNRATED = {
  natija: 0,
  title: 'Qiymatlanmagan',
  range_label: 'Natija yo‘q',
  modal_title: 'Qiymatlanmagan',
  label: 'Qiymatlanmagan',
  summary: 'natija mavjud emas',
  description: 'Talaba bo‘yicha depressiya test natijasi hali tizimga kiritilmagan.',
  guidance: '',
  tone: 'secondary',
  display_order: 99
};

const readGroupId = search => {
  const params = new URLSearchParams(search || '');
  return (params.get('guruh_id') || '').trim();
};

const getFacultyKey = faculty => faculty.code || faculty.name;

const sortByDisplayOrder = items =>
  [...(items || [])].sort((left, right) => Number(left.display_order || 99) - Number(right.display_order || 99));

const buildLevelMap = levels => {
  const map = {};
  sortByDisplayOrder(levels).forEach(level => {
    map[level.natija] = level;
  });
  return map;
};

const mergeLevelStats = (items, levels) => {
  const levelMap = buildLevelMap(levels);

  (items || []).forEach(item => {
    const natija = Number(item.natija);
    if (!levelMap[natija]) {
      return;
    }

    levelMap[natija] = {
      ...levelMap[natija],
      ...item,
      count: Number(item.count || 0),
      percent: Number(item.percent || 0)
    };
  });

  return sortByDisplayOrder(Object.values(levelMap));
};

const resultBadgeClass = tone => `psi-depression-result psi-depression-result--${tone || 'secondary'}`;

const DepressionAnalysis = ({ history, location }) => {
  const [faculties, setFaculties] = useState([]);
  const [segments, setSegments] = useState({ courses: [], nationalities: [] });
  const [levels, setLevels] = useState(DEFAULT_DEPRESSION_LEVELS);
  const [unratedMeta, setUnratedMeta] = useState(DEFAULT_UNRATED);
  const [groupData, setGroupData] = useState(null);
  const [loadingFaculties, setLoadingFaculties] = useState(true);
  const [loadingGroup, setLoadingGroup] = useState(false);
  const [banner, setBanner] = useState(null);
  const [notFoundMessage, setNotFoundMessage] = useState('');
  const [activePeriodLabel, setActivePeriodLabel] = useState('');
  const [groupSearch, setGroupSearch] = useState('');
  const [studentSearch, setStudentSearch] = useState('');
  const [expandedFaculties, setExpandedFaculties] = useState({});
  const [activeLevel, setActiveLevel] = useState(null);
  const [pageSize, setPageSize] = useStoredTablePageSize();

  const groupId = readGroupId(location.search);

  const requestConfig = useMemo(
    () => ({
      headers: getAuthHeaders()
    }),
    []
  );

  const changeGroup = groupName => {
    const search = groupName ? `?guruh_id=${encodeURIComponent(groupName)}` : '';
    history.push({
      pathname: '/depressiya',
      search
    });
  };

  const openLevelDetails = level => {
    if (!level) {
      return;
    }

    setActiveLevel({
      ...level
    });
  };

  useEffect(() => {
    let active = true;

    const fetchFaculties = async () => {
      setLoadingFaculties(true);
      setBanner(null);

      try {
        const response = await axios.get(FACULTIES_ENDPOINT, requestConfig);

        if (!active) {
          return;
        }

        if (response.data && response.data.success) {
          setFaculties(response.data.faculties || []);
          setSegments(response.data.segments || { courses: [], nationalities: [] });
          setLevels(sortByDisplayOrder(response.data.levels || DEFAULT_DEPRESSION_LEVELS));
          setUnratedMeta(response.data.unrated || DEFAULT_UNRATED);
          setActivePeriodLabel(formatSelectedPeriod(response.data.period));
          return;
        }

        throw new Error((response.data && response.data.message) || 'Depressiya ma`lumotlari yuklanmadi.');
      } catch (error) {
        if (!active) {
          return;
        }

        const message =
          (error.response && error.response.data && error.response.data.message) ||
          error.message ||
          'Depressiya ma`lumotlarini yuklashda xatolik yuz berdi.';

        setBanner({ color: 'danger', text: message });
      } finally {
        if (active) {
          setLoadingFaculties(false);
        }
      }
    };

    fetchFaculties();

    return () => {
      active = false;
    };
  }, [requestConfig]);

  useEffect(() => {
    let active = true;

    if (!groupId) {
      setGroupData(null);
      setLoadingGroup(false);
      setNotFoundMessage('');
      return undefined;
    }

    const fetchGroup = async () => {
      setLoadingGroup(true);
      setBanner(null);
      setNotFoundMessage('');

      try {
        const response = await axios.get(GROUP_ENDPOINT, {
          ...requestConfig,
          params: {
            guruh_id: groupId
          }
        });

        if (!active) {
          return;
        }

        if (response.data && response.data.success) {
          setGroupData(response.data.data || null);
          setLevels(sortByDisplayOrder(response.data.levels || DEFAULT_DEPRESSION_LEVELS));
          setUnratedMeta(response.data.unrated || DEFAULT_UNRATED);
          setActivePeriodLabel(formatSelectedPeriod(response.data.period));
          return;
        }

        throw new Error((response.data && response.data.message) || 'Guruh ma`lumotlari yuklanmadi.');
      } catch (error) {
        if (!active) {
          return;
        }

        const status = error.response && error.response.status;
        const message =
          (error.response && error.response.data && error.response.data.message) ||
          error.message ||
          'Guruh ma`lumotlarini yuklashda xatolik yuz berdi.';

        setGroupData(null);

        if (status === 404) {
          setNotFoundMessage(message);
          return;
        }

        setBanner({ color: 'danger', text: message });
      } finally {
        if (active) {
          setLoadingGroup(false);
        }
      }
    };

    fetchGroup();

    return () => {
      active = false;
    };
  }, [groupId, requestConfig]);

  const filteredFaculties = useMemo(() => {
    const search = groupSearch.trim().toLowerCase();

    return faculties
      .map(faculty => {
        const facultyName = (faculty.name || '').toLowerCase();
        const matchesFaculty = facultyName.includes(search);
        const groups = (faculty.groups || []).filter(group => {
          if (!search || matchesFaculty) {
            return true;
          }

          return (group.name || '').toLowerCase().includes(search);
        });

        return {
          ...faculty,
          groups
        };
      })
      .filter(faculty => faculty.groups.length > 0 || (faculty.name || '').toLowerCase().includes(search));
  }, [faculties, groupSearch]);

  useEffect(() => {
    if (!filteredFaculties.length) {
      return;
    }

    setExpandedFaculties(prev => {
      const next = { ...prev };

      if (groupId) {
        const selectedFaculty = filteredFaculties.find(faculty =>
          (faculty.groups || []).some(group => group.name === groupId)
        );

        if (selectedFaculty) {
          next[getFacultyKey(selectedFaculty)] = true;
          return next;
        }
      }

      if (!Object.values(next).some(Boolean)) {
        next[getFacultyKey(filteredFaculties[0])] = true;
      }

      return next;
    });
  }, [filteredFaculties, groupId]);

  const toggleFaculty = facultyKey => {
    setExpandedFaculties(prev => ({
      ...prev,
      [facultyKey]: !prev[facultyKey]
    }));
  };

  const overviewStats = useMemo(() => {
    const aggregated = levels.map(level => ({
      ...level,
      count: 0,
      percent: 0
    }));
    const aggregatedMap = buildLevelMap(aggregated);

    faculties.forEach(faculty => {
      (faculty.stats && faculty.stats.items ? faculty.stats.items : []).forEach(item => {
        const natija = Number(item.natija);
        if (!aggregatedMap[natija]) {
          return;
        }

        aggregatedMap[natija].count += Number(item.count || 0);
      });
    });

    const items = sortByDisplayOrder(Object.values(aggregatedMap));
    const total = items.reduce((sum, item) => sum + Number(item.count || 0), 0);

    return {
      total,
      items: items.map(item => ({
        ...item,
        percent: total > 0 ? Number(((Number(item.count || 0) / total) * 100).toFixed(1)) : 0
      }))
    };
  }, [faculties, levels]);

  const groupStats = useMemo(() => mergeLevelStats(groupData && groupData.stats && groupData.stats.items, levels), [groupData, levels]);

  const filteredStudents = useMemo(() => {
    const students = (groupData && groupData.students) || [];
    const search = studentSearch.trim().toLowerCase();

    if (!search) {
      return students;
    }

    return students.filter(student => {
      const haystack = [
        student.full_name,
        student.group,
        student.result,
        student.range_label,
        student.title,
        student.summary,
        String(student.talaba_id || '')
      ]
        .join(' ')
        .toLowerCase();

      return haystack.includes(search);
    });
  }, [groupData, studentSearch]);

  const columns = useMemo(
    () => [
      {
        dataField: 'order',
        text: 'T/R',
        sort: true,
        classes: 'align-middle font-weight-semi-bold'
      },
      {
        dataField: 'full_name',
        text: 'F.I.O',
        sort: true,
        classes: 'align-middle',
        formatter: (value, row) => (
          <div className="psi-depression-student">
            <div className="psi-depression-student__avatar">{row.initials}</div>
            <div className="psi-depression-student__copy">
              <strong>{value}</strong>
              <span>Talaba ID: {row.talaba_id || '—'}</span>
            </div>
          </div>
        )
      },
      {
        dataField: 'group',
        text: 'Guruh',
        sort: true,
        classes: 'align-middle',
        formatter: value => <span className="psi-depression-group-pill">{value}</span>
      },
      {
        dataField: 'result',
        text: 'Xususiyati',
        sort: true,
        classes: 'align-middle',
        formatter: (value, row) => {
          if (row.is_unrated) {
            return (
              <Badge pill className={resultBadgeClass(row.tone)}>
                {unratedMeta.label}
              </Badge>
            );
          }

          const badgeText = row.range_label;
          const titleText = row.title;
          const noteText = value;

          return (
            <div className="psi-depression-result-cell">
              <Badge pill className={resultBadgeClass(row.tone)}>
                {badgeText}
              </Badge>
              <div className="psi-depression-result-note">
                <strong>{titleText}</strong>
                <span>{noteText}</span>
              </div>
            </div>
          );
        }
      }
    ],
    [unratedMeta]
  );

  const renderLevelBand = (item, key, compact = false) => (
    <div key={key} className={`psi-depression-band psi-depression-band--${item.tone || 'secondary'}${compact ? ' is-compact' : ''}`}>
      <div className="psi-depression-band__head">
        <div className="psi-depression-band__copy">
          <span className="psi-depression-band__range">{item.range_label}</span>
          <strong>{item.title}</strong>
        </div>
        <button type="button" className="psi-depression-band__info" onClick={() => openLevelDetails(item)}>
          i
        </button>
      </div>
      <div className="psi-depression-band__summary">{item.summary}</div>
      <div className="psi-depression-band__progress">
        <span style={{ width: `${Math.max(0, Math.min(100, Number(item.percent || 0)))}%` }} />
      </div>
      <div className="psi-depression-band__meta">
        <span>{item.count} ta</span>
        <strong>{item.percent}%</strong>
      </div>
    </div>
  );

  return (
    <>
      <div className="psi-depression-heading mb-3">
        <div>
          <div className="psi-depression-heading__nav">Tahlillar / Depressiya</div>
          <h3 className="mb-1">Depressiya tahlili</h3>
          <p className="mb-0">Eski dipressiya.php oqimiga mos ravishda fakultet, guruh va talaba kesimidagi holatlarni ko‘ring.</p>
        </div>
        {activePeriodLabel || groupId ? (
          <div className="psi-heading-badges">
            {activePeriodLabel ? <Badge pill className="psi-active-period-badge">{activePeriodLabel}</Badge> : null}
            {groupId ? (
              <Badge pill className="psi-depression-heading__badge">
                {groupId}
              </Badge>
            ) : null}
          </div>
        ) : null}
      </div>

      {banner ? (
        <Alert color={banner.color} className="mb-3">
          {banner.text}
        </Alert>
      ) : null}

      <div className="psi-suicide-toolbar mb-3">
        <div className="psi-suicide-toolbar__copy">
          <h5 className="mb-1">{groupId ? `${groupId} guruhi` : 'Depressiya bo‘yicha guruhlar'}</h5>
          <p className="text-600 mb-0">
            {groupId
              ? 'Tanlangan guruhda barcha talabalar ko‘rsatiladi, natija yo‘q bo‘lsa ham.'
              : 'Fakultetni ochib kerakli guruhni tanlang, statistikalar eski PHP sahifadagi diapazonlarda chiqadi.'}
          </p>
        </div>

        <div className="psi-suicide-toolbar__controls">
          <Input
            value={groupSearch}
            onChange={({ target }) => setGroupSearch(target.value)}
            placeholder="Fakultet yoki guruh qidirish"
            className="psi-suicide-search"
          />
        </div>
      </div>

      <AnalysisSegmentCharts segments={segments} />

      {notFoundMessage ? (
        <Alert color="warning" className="mb-3">
          {notFoundMessage}
        </Alert>
      ) : null}

      <Row className="mb-3 psi-depression-layout">
        <Col xl="5" className="mb-3 mb-xl-0">
          <Card className="border-0 shadow-sm psi-suicide-sidebar-card">
            <CardBody className="psi-suicide-sidebar-card__body">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h5 className="mb-0">Fakultetlar va guruhlar</h5>
                {loadingFaculties ? <Spinner size="sm" color="primary" /> : <Badge color="light">{filteredFaculties.length} ta</Badge>}
              </div>

              <div className="psi-suicide-faculty-list">
                {loadingFaculties ? (
                  <div className="text-center text-500 py-4">Depressiya ma`lumotlari yuklanmoqda...</div>
                ) : filteredFaculties.length > 0 ? (
                  filteredFaculties.map(faculty => {
                    const facultyKey = getFacultyKey(faculty);
                    const isOpen = !!expandedFaculties[facultyKey];
                    const facultyStats = mergeLevelStats(faculty && faculty.stats && faculty.stats.items, levels);

                    return (
                      <div key={facultyKey} className="psi-suicide-faculty">
                        <button
                          type="button"
                          className={`psi-suicide-faculty__header ${isOpen ? 'is-open' : ''}`}
                          onClick={() => toggleFaculty(facultyKey)}
                        >
                          <div className="psi-suicide-faculty__copy">
                            <strong>{`Fakultet ${faculty.name}`}</strong>
                            <span>
                              {faculty.group_count || (faculty.groups || []).length} ta guruh • {faculty.student_count} talaba
                            </span>
                          </div>
                          <span className="psi-suicide-faculty__toggle" aria-hidden="true">
                            {isOpen ? '−' : '+'}
                          </span>
                        </button>

                        {isOpen ? (
                          <div className="psi-suicide-faculty__body">
                            <div className="psi-suicide-mini-stats">
                              {facultyStats.map(item => (
                                <div key={`${facultyKey}-${item.natija}`} className={`psi-suicide-mini-stat psi-suicide-mini-stat--${item.tone}`}>
                                  <div className="psi-suicide-mini-stat__mark">{item.natija}</div>
                                  <div className="psi-suicide-mini-stat__copy">
                                    <strong>{item.count}</strong>
                                    <span>{item.label}</span>
                                  </div>
                                  <small>{item.percent}%</small>
                                </div>
                              ))}
                            </div>

                            <div className="psi-suicide-group-grid">
                              {(faculty.groups || []).map(group => (
                                <button
                                  key={group.name}
                                  type="button"
                                  className={`psi-suicide-group-button ${groupId === group.name ? 'is-active' : ''}`}
                                  onClick={() => changeGroup(group.name)}
                                >
                                  <span>{group.name}</span>
                                  <small>{Number(group.result_count || 0)}/{Number(group.student_count || 0)}</small>
                                </button>
                              ))}
                            </div>
                          </div>
                        ) : null}
                      </div>
                    );
                  })
                ) : (
                  <div className="text-center text-500 py-4">Mos fakultet yoki guruh topilmadi.</div>
                )}
              </div>
            </CardBody>
          </Card>
        </Col>

        <Col xl="7" className="psi-depression-main-col">
          {loadingGroup ? (
            <Card className="border-0 shadow-sm h-100">
              <CardBody className="d-flex align-items-center justify-content-center py-6">
                <div className="text-center">
                  <Spinner color="primary" className="mb-3" />
                  <div className="text-600">Guruh ma`lumotlari yuklanmoqda...</div>
                </div>
              </CardBody>
            </Card>
          ) : groupData ? (
            <div className="psi-depression-main-shell">
              <Row className="mb-3">
                {groupStats.map(item => (
                  <Col md="6" xl="4" key={`${groupData.group}-${item.natija}`} className="mb-3">
                    {renderLevelBand(item, `group-${groupData.group}-${item.natija}`)}
                  </Col>
                ))}
              </Row>

              <Card className="border-0 shadow-sm psi-depression-main-card">
                <CardBody className="psi-depression-main-card__body">
                  <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center mb-3">
                    <div className="mb-3 mb-lg-0">
                      <h5 className="mb-1">{groupData.group} guruhi kesimida</h5>
                      <p className="text-600 mb-0">
                        {groupData.faculty} • {groupData.student_count} ta talaba • {groupData.stats.total} ta baholangan natija
                      </p>
                    </div>
                    <div className="d-flex flex-column flex-sm-row">
                      <Input
                        value={studentSearch}
                        onChange={({ target }) => setStudentSearch(target.value)}
                        placeholder="Talaba qidirish"
                        className="psi-depression-search"
                      />
                    </div>
                  </div>

                  {groupData.stats.total === 0 ? (
                    <Alert color="info" className="mb-3">
                      Bu guruhda depressiya testi hali baholanmagan. Jadvalda barcha talabalar Qiymatlanmagan holatida ko‘rsatiladi.
                    </Alert>
                  ) : null}

                  <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap">
                    <Badge color="light" pill className="px-3 py-2">
                      {filteredStudents.length} ta yozuv
                    </Badge>
                    <TablePageSizeSelect value={pageSize} onChange={setPageSize} className="med-table-page-size mt-2 mt-sm-0" />
                  </div>

                  <div className="psi-depression-table-scroll">
                    <MedDataTable
                      data={filteredStudents}
                      columns={columns}
                      keyField="id"
                      pageSize={pageSize}
                      paginationResetKey={`${groupData.group}-${studentSearch}`}
                      noDataIndication={<div className="text-center py-4 text-500">Bu guruh bo‘yicha talaba topilmadi.</div>}
                      tableClasses="table table-hover fs--1 mb-0 med-dashboard-table psi-depression-table"
                      headerClasses="bg-200 text-900"
                      wrapperClasses="table-responsive psi-depression-table-wrap"
                    />
                  </div>
                </CardBody>
              </Card>
            </div>
          ) : (
            <Card className="border-0 shadow-sm h-100 psi-depression-overview-card">
              <CardBody className="psi-depression-overview-card__body">
                <div className="psi-depression-overview-card__hero">
                  <div>
                    <div className="psi-depression-overview-card__eyebrow">Depressiya taqsimoti</div>
                    <h4 className="mb-1">Fakultetdan guruh tanlang</h4>
                    <p className="text-600 mb-0">
                      Eski PHP sahifadagi kabi 3 diapazon bo‘yicha umumiy ko‘rinish shu yerda chiqadi. Har bir kartadagi i tugmasi batafsil talqinni ochadi.
                    </p>
                  </div>
                  <Badge color="light" pill className="px-3 py-2">
                    {overviewStats.total} ta baholangan natija
                  </Badge>
                </div>

                <Row className="mt-1">
                  {overviewStats.items.map(item => (
                    <Col md="6" xl="4" key={`overview-${item.natija}`} className="mb-3">
                      {renderLevelBand(item, `overview-${item.natija}`)}
                    </Col>
                  ))}
                </Row>
              </CardBody>
            </Card>
          )}
        </Col>
      </Row>

      <Modal isOpen={!!activeLevel} toggle={() => setActiveLevel(null)} centered className="psi-depression-modal">
        <ModalHeader toggle={() => setActiveLevel(null)}>{activeLevel ? activeLevel.modal_title : ''}</ModalHeader>
        <ModalBody>
          {activeLevel ? (
            <div className="psi-depression-modal__content">
              <div className={`psi-depression-modal__pill psi-depression-modal__pill--${activeLevel.tone || 'secondary'}`}>
                {activeLevel.range_label}
              </div>
              <h5 className="mb-2">{activeLevel.title}</h5>
              <p className="mb-3">{activeLevel.description}</p>
              {activeLevel.guidance ? (
                <>
                  <div className="psi-depression-modal__section-title">Qo‘shimcha tavsiya</div>
                  <p className="mb-0">{activeLevel.guidance}</p>
                </>
              ) : null}
            </div>
          ) : null}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setActiveLevel(null)}>
            Yopish
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default DepressionAnalysis;
